import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  InputAdornment,
  Chip,
} from '@mui/material';
import { Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useFamily } from '../../context';
import { FamilySelector, useFamilySelection, FileAttachments } from '../common';
import { updateData } from '../../services/dataApi';

// Allowance Form Dialog
interface AllowanceData {
  personId: string;
  amount: number;
  frequency: 'weekly' | 'biweekly' | 'monthly';
  dayOfWeek?: string;
  notes?: string;
}

interface AllowanceFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: AllowanceData, familyIds: string[]) => Promise<void>;
  onDelete?: () => Promise<void>;
  allowance?: { id: string; data?: { data?: AllowanceData } } | null;
}

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const AllowanceFormDialog: React.FC<AllowanceFormDialogProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  allowance,
}) => {
  const { memberList, getMemberName, getMemberColor } = useFamily();
  const isEditing = !!allowance;
  const {
    selectedFamilyIds,
    setSelectedFamilyIds,
    showSelector: showFamilySelector,
  } = useFamilySelection();

  const [personId, setPersonId] = useState('');
  const [amount, setAmount] = useState('');
  const [frequency, setFrequency] = useState<'weekly' | 'biweekly' | 'monthly'>('weekly');
  const [dayOfWeek, setDayOfWeek] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (allowance) {
        const data = (allowance.data?.data || {}) as any;
        setPersonId(data.personId || '');
        setAmount(data.amount?.toString() || '');
        setFrequency(data.frequency || 'weekly');
        setDayOfWeek(data.dayOfWeek || data.day_of_week || '');
        setNotes(data.notes || '');
      } else {
        setPersonId(memberList[0]?.id || '');
        setAmount('');
        setFrequency('weekly');
        setDayOfWeek('');
        setNotes('');
      }
      setError(null);
    }
  }, [open, allowance, memberList]);

  const handleSave = async () => {
    if (!personId) {
      setError('Please select a family member');
      return;
    }
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave({
        personId,
        amount: amountNum,
        frequency,
        dayOfWeek: dayOfWeek || undefined,
        notes: notes.trim() || undefined,
      }, selectedFamilyIds);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save allowance');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete allowance');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{isEditing ? 'Edit Allowance' : 'New Allowance'}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Grid container spacing={2}>
          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel>Family Member</InputLabel>
              <Select value={personId} label="Family Member" onChange={(e) => setPersonId(e.target.value)}>
                {memberList.map((member) => (
                  <MenuItem key={member.id} value={member.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, bgcolor: getMemberColor(member.id), fontSize: 12 }}>
                        {getMemberName(member.id).charAt(0)}
                      </Avatar>
                      {getMemberName(member.id)}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {showFamilySelector && !isEditing && (
            <Grid size={12}>
              <FamilySelector value={selectedFamilyIds} onChange={setSelectedFamilyIds} />
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} fullWidth required type="number" InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Frequency</InputLabel>
              <Select value={frequency} label="Frequency" onChange={(e) => setFrequency(e.target.value as any)}>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="biweekly">Bi-Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {(frequency === 'weekly' || frequency === 'biweekly') && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>Day of Week</InputLabel>
                <Select value={dayOfWeek} label="Day of Week" onChange={(e) => setDayOfWeek(e.target.value)}>
                  <MenuItem value=""><em>Not set</em></MenuItem>
                  {DAYS_OF_WEEK.map((day) => (
                    <MenuItem key={day} value={day}>{day}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid size={12}>
            <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline rows={2} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {isEditing && onDelete && (
          <Button onClick={handleDelete} color="error" startIcon={<DeleteIcon />} disabled={saving} sx={{ mr: 'auto' }}>Delete</Button>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Expense Form Dialog
const EXPENSE_CATEGORIES = [
  { key: 'entertainment', label: 'Entertainment' },
  { key: 'food', label: 'Food' },
  { key: 'education', label: 'Education' },
  { key: 'toys', label: 'Toys' },
  { key: 'clothes', label: 'Clothes' },
  { key: 'healthcare', label: 'Healthcare' },
  { key: 'activities', label: 'Activities' },
  { key: 'transportation', label: 'Transportation' },
  { key: 'other', label: 'Other' },
];

interface ExpenseData {
  name: string;
  amount: number;
  category?: string;
  date?: string;
  paidBy?: string;
  splitMembers?: string[];
  splitType?: 'equal' | 'custom';
  customSplits?: Record<string, number>;
  notes?: string;
  fileIds?: string[];
}

interface ExpenseFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: ExpenseData, familyIds: string[]) => Promise<void>;
  onDelete?: () => Promise<void>;
  expense?: { id: string; data?: { data?: ExpenseData } } | null;
}

export const ExpenseFormDialog: React.FC<ExpenseFormDialogProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  expense,
}) => {
  const { memberList, getMemberName, getMemberColor } = useFamily();
  const isEditing = !!expense;
  const {
    selectedFamilyIds,
    setSelectedFamilyIds,
    showSelector: showFamilySelector,
  } = useFamilySelection();

  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [date, setDate] = useState('');
  const [paidBy, setPaidBy] = useState('');
  const [splitMembers, setSplitMembers] = useState<string[]>([]);
  const [splitType, setSplitType] = useState<'equal' | 'custom'>('equal');
  const [customSplits, setCustomSplits] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState('');
  const [fileIds, setFileIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (expense) {
        const data = (expense.data?.data || {}) as any;
        setName(data.name || data.description || '');
        setAmount(data.amount?.toString() || '');
        setCategory(data.category || '');
        setDate(data.date || '');
        setPaidBy(data.paidBy || data.paid_by_member_id || '');
        setSplitMembers(data.splitMembers || data.selectedMembers || []);
        setSplitType(data.splitType || 'equal');
        const splits: Record<string, string> = {};
        if (data.customSplits) {
          Object.entries(data.customSplits).forEach(([k, v]) => { splits[k] = String(v); });
        }
        setCustomSplits(splits);
        setNotes(data.notes || '');
        setFileIds(data.fileIds || []);
      } else {
        setName('');
        setAmount('');
        setCategory('');
        setDate(new Date().toISOString().split('T')[0]);
        setPaidBy('');
        setSplitMembers([]);
        setSplitType('equal');
        setCustomSplits({});
        setNotes('');
        setFileIds([]);
      }
      setError(null);
    }
  }, [open, expense]);

  const toggleSplitMember = (memberId: string) => {
    setSplitMembers((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Description is required');
      return;
    }
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const parsedSplits: Record<string, number> = {};
      if (splitType === 'custom') {
        Object.entries(customSplits).forEach(([k, v]) => {
          const num = parseFloat(v);
          if (!isNaN(num)) parsedSplits[k] = num;
        });
      }

      await onSave({
        name: name.trim(),
        amount: amountNum,
        category: category || undefined,
        date: date || undefined,
        paidBy: paidBy || undefined,
        splitMembers: splitMembers.length > 0 ? splitMembers : undefined,
        splitType: splitMembers.length > 0 ? splitType : undefined,
        customSplits: splitType === 'custom' && Object.keys(parsedSplits).length > 0 ? parsedSplits : undefined,
        notes: notes.trim() || undefined,
        fileIds: fileIds.length > 0 ? fileIds : undefined,
      }, selectedFamilyIds);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete expense');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{isEditing ? 'Edit Expense' : 'New Expense'}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField label="Description" value={name} onChange={(e) => setName(e.target.value)} fullWidth required autoFocus />
          </Grid>

          {showFamilySelector && !isEditing && (
            <Grid size={12}>
              <FamilySelector value={selectedFamilyIds} onChange={setSelectedFamilyIds} />
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} fullWidth required type="number" InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select value={category} label="Category" onChange={(e) => setCategory(e.target.value)}>
                <MenuItem value=""><em>None</em></MenuItem>
                {EXPENSE_CATEGORIES.map((cat) => (
                  <MenuItem key={cat.key} value={cat.key}>{cat.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Paid By</InputLabel>
              <Select value={paidBy} label="Paid By" onChange={(e) => setPaidBy(e.target.value)}>
                <MenuItem value=""><em>None</em></MenuItem>
                {memberList.map((member) => (
                  <MenuItem key={member.id} value={member.id}>{getMemberName(member.id)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Split Members */}
          <Grid size={12}>
            <Typography variant="subtitle2" gutterBottom>Split Between</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {memberList.map((member) => {
                const isSelected = splitMembers.includes(member.id);
                const memberName = getMemberName(member.id);
                const color = getMemberColor(member.id);
                return (
                  <Chip
                    key={member.id}
                    avatar={<Avatar sx={{ bgcolor: color }}>{memberName.charAt(0)}</Avatar>}
                    label={memberName}
                    onClick={() => toggleSplitMember(member.id)}
                    variant={isSelected ? 'filled' : 'outlined'}
                    color={isSelected ? 'primary' : 'default'}
                    sx={{ cursor: 'pointer' }}
                  />
                );
              })}
            </Box>
          </Grid>

          {splitMembers.length > 0 && (
            <>
              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel>Split Type</InputLabel>
                  <Select value={splitType} label="Split Type" onChange={(e) => setSplitType(e.target.value as any)}>
                    <MenuItem value="equal">Split Equally</MenuItem>
                    <MenuItem value="custom">Custom Split</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {splitType === 'custom' && splitMembers.map((memberId) => (
                <Grid size={{ xs: 12, sm: 6 }} key={memberId}>
                  <TextField
                    label={getMemberName(memberId)}
                    value={customSplits[memberId] || ''}
                    onChange={(e) => setCustomSplits((prev) => ({ ...prev, [memberId]: e.target.value }))}
                    fullWidth
                    type="number"
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  />
                </Grid>
              ))}
            </>
          )}

          {/* Receipt / Attachments */}
          <Grid size={12}>
            <FileAttachments
              fileIds={fileIds}
              onChange={setFileIds}
              onPersist={expense ? async (ids) => {
                const data = expense.data?.data || {};
                await updateData(expense.id, 'expense', { ...data, fileIds: ids });
              } : undefined}
              dataId={expense?.id}
              canUpload={isEditing}
              label="Receipt"
            />
          </Grid>

          <Grid size={12}>
            <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline rows={2} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {isEditing && onDelete && (
          <Button onClick={handleDelete} color="error" startIcon={<DeleteIcon />} disabled={saving} sx={{ mr: 'auto' }}>Delete</Button>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Savings Goal Form Dialog
const GOAL_ICONS = [
  { key: 'bike', label: 'Bike' },
  { key: 'gamepad', label: 'Game' },
  { key: 'palette', label: 'Art' },
  { key: 'home', label: 'House' },
  { key: 'headphones', label: 'Music' },
  { key: 'teddy-bear', label: 'Toy' },
  { key: 'basketball', label: 'Sports' },
  { key: 'star', label: 'Other' },
];

interface SavingsGoalData {
  name: string;
  targetAmount: number;
  currentAmount?: number;
  personId?: string;
  deadline?: string;
  icon?: string;
  notes?: string;
}

interface SavingsGoalFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: SavingsGoalData, familyIds: string[]) => Promise<void>;
  onDelete?: () => Promise<void>;
  savingsGoal?: { id: string; data?: { data?: SavingsGoalData } } | null;
}

export const SavingsGoalFormDialog: React.FC<SavingsGoalFormDialogProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  savingsGoal,
}) => {
  const { memberList, getMemberName, getMemberColor } = useFamily();
  const isEditing = !!savingsGoal;
  const {
    selectedFamilyIds,
    setSelectedFamilyIds,
    showSelector: showFamilySelector,
  } = useFamilySelection();

  const [name, setName] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [currentAmount, setCurrentAmount] = useState('');
  const [personId, setPersonId] = useState('');
  const [deadline, setDeadline] = useState('');
  const [icon, setIcon] = useState('star');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (savingsGoal) {
        const data = (savingsGoal.data?.data || {}) as any;
        setName(data.name || '');
        setTargetAmount(data.targetAmount?.toString() || data.target?.toString() || '');
        setCurrentAmount(data.currentAmount?.toString() || data.saved?.toString() || '0');
        setPersonId(data.personId || '');
        setDeadline(data.deadline || '');
        setIcon(data.icon || 'star');
        setNotes(data.notes || '');
      } else {
        setName('');
        setTargetAmount('');
        setCurrentAmount('0');
        setPersonId('');
        setDeadline('');
        setIcon('star');
        setNotes('');
      }
      setError(null);
    }
  }, [open, savingsGoal]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Goal name is required');
      return;
    }
    const targetNum = parseFloat(targetAmount);
    if (isNaN(targetNum) || targetNum <= 0) {
      setError('Please enter a valid target amount');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave({
        name: name.trim(),
        targetAmount: targetNum,
        currentAmount: parseFloat(currentAmount) || 0,
        personId: personId || undefined,
        deadline: deadline || undefined,
        icon,
        notes: notes.trim() || undefined,
      }, selectedFamilyIds);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save savings goal');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete savings goal');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{isEditing ? 'Edit Savings Goal' : 'New Savings Goal'}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Grid container spacing={2}>
          {/* Icon selector */}
          <Grid size={12}>
            <Typography variant="subtitle2" gutterBottom>Icon</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {GOAL_ICONS.map((gi) => (
                <Chip
                  key={gi.key}
                  label={gi.label}
                  onClick={() => setIcon(gi.key)}
                  variant={icon === gi.key ? 'filled' : 'outlined'}
                  color={icon === gi.key ? 'primary' : 'default'}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Grid>

          <Grid size={12}>
            <TextField label="Goal Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth required autoFocus placeholder="e.g., New Bike, College Fund" />
          </Grid>

          {showFamilySelector && !isEditing && (
            <Grid size={12}>
              <FamilySelector value={selectedFamilyIds} onChange={setSelectedFamilyIds} />
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Target Amount" value={targetAmount} onChange={(e) => setTargetAmount(e.target.value)} fullWidth required type="number" InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Current Amount" value={currentAmount} onChange={(e) => setCurrentAmount(e.target.value)} fullWidth type="number" InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>For</InputLabel>
              <Select value={personId} label="For" onChange={(e) => setPersonId(e.target.value)}>
                <MenuItem value=""><em>Family</em></MenuItem>
                {memberList.map((member) => (
                  <MenuItem key={member.id} value={member.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, bgcolor: getMemberColor(member.id), fontSize: 12 }}>{getMemberName(member.id).charAt(0)}</Avatar>
                      {getMemberName(member.id)}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Target Date" type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>

          <Grid size={12}>
            <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline rows={2} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {isEditing && onDelete && (
          <Button onClick={handleDelete} color="error" startIcon={<DeleteIcon />} disabled={saving} sx={{ mr: 'auto' }}>Delete</Button>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Payment Form Dialog
interface PaymentData {
  fromMemberId: string;
  toMemberId: string;
  amount: number;
  date?: string;
  notes?: string;
}

interface PaymentFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: PaymentData, familyIds: string[]) => Promise<void>;
  onDelete?: () => Promise<void>;
  payment?: { id: string; data?: { data?: PaymentData } } | null;
}

export const PaymentFormDialog: React.FC<PaymentFormDialogProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  payment,
}) => {
  const { memberList, getMemberName, getMemberColor } = useFamily();
  const isEditing = !!payment;
  const {
    selectedFamilyIds,
    setSelectedFamilyIds,
    showSelector: showFamilySelector,
  } = useFamilySelection();

  const [fromMemberId, setFromMemberId] = useState('');
  const [toMemberId, setToMemberId] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (payment) {
        const data = (payment.data?.data || {}) as any;
        setFromMemberId(data.fromMemberId || '');
        setToMemberId(data.toMemberId || '');
        setAmount(data.amount?.toString() || '');
        setDate(data.date || '');
        setNotes(data.notes || '');
      } else {
        setFromMemberId('');
        setToMemberId('');
        setAmount('');
        setDate(new Date().toISOString().split('T')[0]);
        setNotes('');
      }
      setError(null);
    }
  }, [open, payment]);

  const handleSave = async () => {
    if (!fromMemberId) {
      setError('Please select who the payment is from');
      return;
    }
    if (!toMemberId) {
      setError('Please select who the payment is to');
      return;
    }
    if (fromMemberId === toMemberId) {
      setError('From and To cannot be the same person');
      return;
    }
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave({
        fromMemberId,
        toMemberId,
        amount: amountNum,
        date: date || undefined,
        notes: notes.trim() || undefined,
      }, selectedFamilyIds);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save payment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{isEditing ? 'Edit Payment' : 'New Payment'}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Grid container spacing={2}>
          {showFamilySelector && !isEditing && (
            <Grid size={12}>
              <FamilySelector value={selectedFamilyIds} onChange={setSelectedFamilyIds} />
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required>
              <InputLabel>From</InputLabel>
              <Select value={fromMemberId} label="From" onChange={(e) => setFromMemberId(e.target.value)}>
                {memberList.map((member) => (
                  <MenuItem key={member.id} value={member.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, bgcolor: getMemberColor(member.id), fontSize: 12 }}>{getMemberName(member.id).charAt(0)}</Avatar>
                      {getMemberName(member.id)}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required>
              <InputLabel>To</InputLabel>
              <Select value={toMemberId} label="To" onChange={(e) => setToMemberId(e.target.value)}>
                {memberList.map((member) => (
                  <MenuItem key={member.id} value={member.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, bgcolor: getMemberColor(member.id), fontSize: 12 }}>{getMemberName(member.id).charAt(0)}</Avatar>
                      {getMemberName(member.id)}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} fullWidth required type="number" InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>

          <Grid size={12}>
            <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline rows={2} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {isEditing && onDelete && (
          <Button onClick={handleDelete} color="error" startIcon={<DeleteIcon />} disabled={saving} sx={{ mr: 'auto' }}>Delete</Button>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default {
  AllowanceFormDialog,
  ExpenseFormDialog,
  SavingsGoalFormDialog,
  PaymentFormDialog,
};
